import { AlertCircle, Home, RefreshCcw } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from "react-router-dom";

import { Button } from "@/components/ui/button";

/**
 * ``errorElement`` global do React Router. Substitui o overlay padrao
 * ``Unexpected Application Error!`` por uma UI alinhada com o design
 * system + dois CTAs (recarregar / voltar pro inicio).
 *
 * Captura tres tipos de erro:
 *  - ``isRouteErrorResponse(err)``: respostas HTTP elevadas pelo router
 *    (ex.: ``loader`` throw new Response(...)).
 *  - ``Error`` lancado de qualquer componente da subtree -- incluindo
 *    violacao de regras dos hooks ("Rendered more hooks than during
 *    the previous render"), erros de render condicional, etc.
 *  - qualquer outro valor (string, objeto custom).
 *
 * Sem o errorElement, o React Router renderiza um overlay fora da
 * arvore do AuthProvider/QueryClientProvider, que apresenta UI feia
 * + impede que o usuario recupere a sessao sem reload manual.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  // Loga no console para o dev conseguir investigar -- o RouterProvider
  // engole por padrao.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary] caught", error);
  }, [error]);

  let title = t("errors.boundary.title", "Algo deu errado");
  let description = t(
    "errors.boundary.description",
    "Uma falha inesperada interrompeu o carregamento desta tela. Tente recarregar a pagina ou voltar ao inicio.",
  );

  if (isRouteErrorResponse(error)) {
    title = `${error.status} ${error.statusText || ""}`.trim();
    if (error.data && typeof error.data === "object" && "message" in error.data) {
      description = String((error.data as { message: unknown }).message);
    }
  } else if (error instanceof Error) {
    description = error.message || description;
  }

  return (
    <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-4 p-8 text-center">
      <AlertCircle className="h-12 w-12 text-danger-500" aria-hidden />
      <div className="space-y-1">
        <h1 className="text-xl font-semibold text-ink-950">{title}</h1>
        <p className="mx-auto max-w-md text-sm text-ink-500">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => {
            // Reload completo recupera de estados quebrados (ex.: cache
            // do TanStack Query corrompido, HMR perdendo context).
            window.location.reload();
          }}
        >
          <RefreshCcw className="mr-1.5 h-4 w-4" />
          {t("errors.boundary.reload", "Recarregar")}
        </Button>
        <Button
          onClick={() => {
            navigate("/");
          }}
        >
          <Home className="mr-1.5 h-4 w-4" />
          {t("errors.boundary.home", "Voltar ao inicio")}
        </Button>
      </div>
      {import.meta.env.DEV && error instanceof Error && error.stack ? (
        <details className="mt-4 max-w-2xl text-left text-xs text-ink-500">
          <summary className="cursor-pointer text-ink-700">
            {t("errors.boundary.devDetails", "Detalhes tecnicos (dev only)")}
          </summary>
          <pre className="mt-2 overflow-x-auto rounded-md bg-canvas/60 p-3 font-mono text-[11px] leading-relaxed">
            {error.stack}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
